import { computed } from "vue";
import { queryTeamInfo, queryTeamUserInfo, TeamInfo, TeamUserInfo } from "@/views/group/service";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { defineStore } from "pinia";
import { useUserStore } from "@/store/user";
import { get } from "lodash";
import { exitTeamUser, retainTeamMembers } from "@/api/group";
import { useTeamEditionStore } from "@/store/teamEdition";
import { userRightStore } from "@/hooks/useUserRights";
import { useRouter } from "vue-router";

export enum PeriodType {
  Official = 1,
  Trial = 2,
  Pending = 3
}

/**
 *
 * 团队信息store
 *
 */
const useGroupStore = defineStore("groupStore", () => {
  const userStore = useUserStore();
  const queryClient = useQueryClient();
  const router = useRouter();

  const { data, refetch: initGroup } = useQuery({
    queryKey: ["queryTeamInfo"],
    queryFn: () => queryTeamInfo(),
    enabled: userStore.isGroupLogin,
    retry: false
  });

  const {
    data: groupMembersRes,
    refetch: getGroupMemberList,
    isLoading
  } = useQuery({
    queryKey: ["queryTeamUserInfo"],
    queryFn: () => queryTeamUserInfo({ pageNo: 1, pageSize: 1000 }),
    enabled: userStore.isGroupLogin,
    retry: false,
    refetchOnWindowFocus: false
  });

  const groupState = computed<TeamInfo | undefined>(() => get(data.value, "data"));

  /**periodType 1正式版 2试用版 3待定版 */
  const periodType = computed(() => get(groupState.value, "periodType", 2));

  /**
   * 团队类型：pro-标准版会员 plus-专业版会员 ultra-旗舰版会员
   */

  const memberType = computed(() => groupState.value?.memberType);

  // 团队成员
  const groupMembers = computed<TeamUserInfo[]>(() =>
    get(groupMembersRes.value, "data.teamUserPage.records", []).map((item, index) => ({ ...item, index: index + 1 }))
  );

    /** 就是不正常版本不是过期 */
    const isUnnormal = computed(() => periodType.value == 3);

  /**
   * 座位状态
   *  all: 总座位数, occupied: 已占座位数 ,left: 剩余座位数 , pre: 续订座位数,   leftover :剩余续订人数,  current:当前团队人数
   */
  const seatState = computed(() => {
    let data = get(groupMembersRes.value, "data", { headCount: 0, joinCount: 0, leftCount: 0, preparHeadCount: 0, preparLeftCount: 0, current: 0 });
    return { all: data.headCount, occupied: data.joinCount, left: data.leftCount, pre: data.preparHeadCount, leftover: data.preparLeftCount, current: isUnnormal.value ? data.joinCount : data.headCount };
  });

  // 是否是超级管理员
  const isSuperAdmin = computed(() => groupState.value?.role == 1);

  // 是否过期
  const isExpired = computed(() => groupState.value?.expire);

  /** 是否为团队试用版 */
  const isTrialVersion = computed(() => periodType.value == 2);

  //自己退出
  async function selfExit() {
    try {
      await exitTeamUser();

      const userRightsHook = userRightStore();
      await userRightsHook.refreshUserRights();

      const teamEditionStore = useTeamEditionStore();
      /**通用方法没有聚合的问题,不够干净 */
      teamEditionStore.setModalOpen(false);
      teamEditionStore.userType = "personal";
      window.onbeforeunload = null;
      localStorage.setItem("teamEditionUserType", "personal");

      await router.replace({ name: "workbench" });
      location.reload();
    } catch (error) {
      throw error;
    }
  }
  /**刷新团队信息 */

  async function refreshGroup() {
    Promise.all([await initGroup(), await getGroupMemberList()]).then(() => null);
  }

  /**保留当前成员 */

  async function retainCurrentMember(teamUserIds: any[]) {
    try {
      await retainTeamMembers({ teamUserIds });
      const userRightsHook = userRightStore();
      await userRightsHook.refreshUserRights();

      refreshGroup();
    } catch (error) {
      throw error;
    }
  }

  /**清理团队相关缓存 */
  function clearGroupCache() {
    queryClient.removeQueries({ queryKey: ["queryTeamInfo"] });
    queryClient.removeQueries({ queryKey: ["queryTeamUserInfo"] });
  }

  /**重新初始化团队信息（用于用户重新登录后） */
  async function reinitializeGroup() {
    if (userStore.isGroupLogin) {
      await refreshGroup();
    }
  }

  return {
    groupState,
    /** 是否过期 */
    isExpired,
    isTrialVersion,
    isUnnormal,
    groupMembersRes,
    memberType,
    groupMembers,
    seatState,
    isSuperAdmin,
    loading: isLoading,
    initGroup,
    getGroupMemberList,
    selfExit,
    refreshGroup,
    retainCurrentMember,
    clearGroupCache,
    reinitializeGroup,
   /**periodType 1正式版 2试用版 3待定版 */
    periodType
  };
});

export default useGroupStore;
